import GenerationSummary from "../components/molecules/AIAssistance/AIGenerationSummary";
import { formatDateToYYYYMMDDHHMMSS, generateMongoId } from "./commonUtils";
import { getDataFetchTransactions, getFunctionalTransactions } from "./transactionUtils";


// Normalize incoming items into a renderable shape:
// - { type: 'array', value: [...] }
// - { type: 'string', value: "..." }
export const normalize = ({ raw, entity }) => {
  if (Array.isArray(raw)) return raw;

  if (typeof raw === "string") {
    // Try to parse a JSON array string (e.g. pass-through from backend).
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {
      // not JSON — fall through to render as plain string
    }
    let value = raw;
    return value;
  }

  // fallback: render nothing
  return [];
};



/**
 * Checks whether two arrays of objects differ in:
 * - length
 * - order
 * - key values
 *
 * @param {Array<Object>} firstArray
 * @param {Array<Object>} secondArray
 * @returns {boolean} true if any difference exists, otherwise false
 */
export const hasArrayDifference = (firstArray, secondArray) => {
  try {


    if (!Array.isArray(firstArray) || !Array.isArray(secondArray)) {
      throw new TypeError("Both arguments must be arrays");
    }

    if (firstArray.length !== secondArray.length) {
      return true;
    }

    return firstArray.some((firstItem, index) => {
      const secondItem = secondArray[index];

      if (!isSameObjectShape(firstItem, secondItem)) {
        return true;
      }

      return !areObjectsEqual(firstItem, secondItem);
    });

  } catch (error) {
    console.error("Comparing arrays failed. ", error);
    return false;
  }
}

/**
 * Checks whether two objects have the same keys
 */
function isSameObjectShape(obj1, obj2) {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  return keys1.every(key => Object.prototype.hasOwnProperty.call(obj2, key));
}

/**
 * Checks whether two objects have identical primitive values
 */
function areObjectsEqual(obj1, obj2) {
  return Object.keys(obj1).every(
    key => obj1[key] === obj2[key]
  );
}



export function generateUniqueEventName(event, existingNames) {
  if (!event || !event.slug_name) {
    throw new Error("Event name is required");
  }

  const baseName = event.slug_name;

  // Fast lookup
  const nameSet = new Set(existingNames);

  // If no conflict, use as-is
  if (!nameSet.has(baseName)) {
    existingNames.push(baseName);
    return baseName;
  }

  // Find next available suffix
  let counter = 1;
  let newName = `${baseName}_${counter}`;

  while (nameSet.has(newName)) {
    counter++;
    newName = `${baseName}_${counter}`;
  }

  existingNames.push(newName);
  return newName;
}




export function buildChatHistory({ step, logs = [] }) {
  try {

    switch (step) {
      case "transaction":
        return buildTransactionChatHistory(logs);
      case "database_profile":
        return buildDatabaseChatHistory(logs);
      case "view":
        return buildViewChatHistory(logs);
      case "simulation":
        return buildSimulationChatHistory(logs);
      default:
        return buildSystemModelChatHistory(logs);
    }

  } catch (error) {
    throw error;
  }
}



const generateUserChat = ({ log, type }) => {
  const userChat = {
    id: generateMongoId(),
    type,
    role: 'user',
    status: true,
    content: log.userPrompt,
    time: log.user_prompt_timestamp || log.final_response_timestamp || log.createdAt
  };

  if (log.updatedEntity) userChat.stepTitle = `@${log.updatedEntity}`;
  return userChat;
};

const generateStoppedChat = ({log, type}) => {
  const stoppedChat = {
    id: generateMongoId(),
    role: "ai",
    status: true,
    content: log.llm_response || "Generation cancelled. Partial results may be incomplete.",
    type,
    time: log.final_response_timestamp
  }
  return stoppedChat;
}

const generateFailedChat = ({ log, type }) => {
  const errorCode = log.llm_response?.error?.code;

  let content = "AI generation failed. Please try again";
  if (errorCode === "rate_limit_exceeded") {
    content = "Too many requests. Please wait a moment and try again."
    console.log("Rate limit reached (requests/min or tokens/min)");
  } else if (errorCode === "insufficient_quota") {
    content = "AI generation is temporarily unavailable. Please contact your administrator."
    console.log("Billing quota exhausted");
  } else if (errorCode === "context_length_exceeded") {
    console.log("Token limit exceeded for this model");
  } else if (errorCode === "CONFIG_ISSUE") {
    content = "AI generation is currently unavailable due to a configuration issue. Please contact your administrator"
  }
  return {
    id: generateMongoId(),
    type,
    role: 'ai',
    status: false,
    content,
    time: log.user_prompt_timestamp || log.createdAt
  };
};

const buildTransactionChatHistory = (logs) => {
  const history = [];
  logs.forEach(log => {
    const response = log.llm_response;
   
    if(log.type === "transaction_clarity_check"){
      const llmResponse = log.llm_response
      // history.push(generateUserChat({ log, type: "view" }));
      if(llmResponse.question){
        if(log.userPrompt){
          history.push(
            generateUserChat({ log, type: "transaction" })
          );
        }
        history.push({
          id: String(log._id),
          role: "ai",
          status: true,
          content: typeof response === "string" ? response : llmResponse?.question?.text,
          type: "system_model",
          time: log.final_response_timestamp
        })
      }else if(llmResponse.summary){
        if(log.userPrompt){
          history.push(
            generateUserChat({ log, type: "transaction" })
          );
        }
        history.push({
          id: String(log._id),
          role: "ai",
          status: true,
          children: <GenerationSummary 
            title="Generation Summary"
            sections={[
              {
                type: 'prompt',
                label: "From your input",
                rows: llmResponse.summary?.from_prompt?.map(d => ({key: d.label, value: d.value}))
              },
              {
                type: 'answers',
                label: "From your answers",
                rows: llmResponse.summary?.from_answers?.map(d => ({key: d.label, value: d.value}))
              },
              {
                type: 'assumed',
                label: "Assumed by AI",
                rows: llmResponse.summary?.assumed_by_ai?.map(d => ({key: d.label, value: d.value}))
              }
            ]}
          />,
          type: "summary",
          time: log.final_response_timestamp
        })
      } else if (log.status === "stopped") {
        history.push(generateUserChat({ log, type: "transaction" }));
        history.push(generateStoppedChat({log, type: "transaction"}));
      }
    }else if (log.type === "transaction_list") {
        if (log.status === "failed") {
          history.push(generateFailedChat({ log, type: "transaction" }));
        } else {
          history.push({
            id: String(log._id),
            role: 'ai',
            status: true,
            content: typeof response === "string" ? response : log.llm_response.transactions?.length === 0 ? 'No results generated. Try refining your request.' : (log.llm_response.transactions || []),
            type: 'transaction',
            time: log.final_response_timestamp
          });
        }
    } else if (log.type === "transaction" && log.action === "update") {
      if (log.userPrompt != null) {
        history.push(generateUserChat({ log, type: "transaction" }));
        if (log.status === "failed") {
          history.push(generateFailedChat({ log, type: "transaction" }));
        } else {


          history.push({
            id: String(log._id),
            role: 'ai',
            status: true,
            content: typeof response === "string" ? response : "Transaction updated",
            type: 'transaction',
            time: log.final_response_timestamp
          });
        }
      }
    }
  });
  return history;
}


const buildDatabaseChatHistory = (logs) => {
  const history = [];
  logs.forEach(log => {
    if (log.userPrompt != null) {
      history.push(generateUserChat({ log, type: "database_profile" }));
      if (log.status === "failed") {
        history.push(generateFailedChat({ log, type: "database_profile" }));
      } else {
        if (log.type === "database_sample_data") {
          if (log.status === "stopped") {
            history.push(generateStoppedChat({log, type: "database_profile"}));
          } else {
            history.push({
              id: String(log._id),
              role: 'ai',
              status: true,
              content: "Sample data generated",
              type: 'database_profile',
              download: true,
              time: log.final_response_timestamp
            });
          }
        } else {
          const response = log.llm_response;

          history.push({
            id: String(log._id),
            role: 'ai',
            status: true,
            content: typeof response === "string" ? response : response?.tables?.length === 0 ? 'No results generated. Try refining your request.' : (log.llm_response.tables || []),
            type: 'database_profile',
            time: log.final_response_timestamp
          });
        }
      }
    }
  });
  
  return history;
}

const buildViewChatHistory = (logs) => {
 
  const history = [];
  logs.forEach(log => {
    const response = log.llm_response;

    if(log.type === "views_clarity_check"){
      const llmResponse = log.llm_response

      if(llmResponse.question){
        if(log.userPrompt){
          history.push(
            generateUserChat({ log, type: "views" })
          );
        }
        history.push({
          id: String(log._id),
          role: "ai",
          status: true,
          content: typeof response === "string" ? response : llmResponse?.question?.text,
          type: "system_model",
          time: log.final_response_timestamp
        })
      }else if(llmResponse.summary){
        if(log.userPrompt){
          history.push(
            generateUserChat({ log, type: "views" })
          );
        }
        history.push({
          id: String(log._id),
          role: "ai",
          status: true,
          children: <GenerationSummary 
            title="Generation Summary"
            sections={[
              {
                type: 'prompt',
                label: "From Your input",
                rows: llmResponse.summary?.from_prompt?.map(d => ({key: d.label, value: d.value}))
              },
              {
                type: 'answers',
                label: "From your answers",
                rows: llmResponse.summary?.from_answers?.map(d => ({key: d.label, value: d.value}))
              },
              {
                type: 'assumed',
                label: "Assumed by AI",
                rows: llmResponse.summary?.assumed_by_ai?.map(d => ({key: d.label, value: d.value}))
              }
            ]}
          />,
          type: "summary",
          time: log.final_response_timestamp
        })
      } else if (log.status === "stopped") {
        history.push(generateUserChat({ log, type: "views" }));
        history.push(generateStoppedChat({log, type: "views"}));
      }
    }else if (log.type === "views_list") {
      if (log.status === "failed") {
        history.push(generateFailedChat({ log, type: "view" }));
      } else {
        history.push({
          id: String(log._id),
          role: 'ai',
          status: true,
          content: typeof response === "string" ? response : response?.views?.length === 0 ? 'No results generated. Try refining your request.' : log.llm_response.views || [],
          type: 'view',
          time: log.final_response_timestamp
        });
      }
    } else if (log.type === "views" && log.action === "update") {
        if (log.status === "failed") {
          history.push(generateFailedChat({ log, type: "view" }));
        } else {
          history.push({
            id: String(log._id),
            role: 'ai',
            status: true,
            content:  typeof response === "string" ? response : "View updated",
            type: 'view',
            time: log.final_response_timestamp
          });
        }
    }
  });
  return history;
}

const buildSystemModelChatHistory = (logs = []) => {
  const FIELD_MAP = {
    digital_twins: "Digital Twins",
    clusterlets: "Clusterlets",
    delegates: "Delegates",
    enterpriseApps: "Enterprise Apps"
  };

  let lastResponse = 'others'
  return logs.reduce((history, log) => {
    
    if (log.status === "stopped") {
      history.push(generateUserChat({ log, type: "system_model" }));
      history.push(generateStoppedChat({log, type: "system_model"}));
      return history;
    }

    if (log.userPrompt == null && lastResponse !== 'summary') return history;
    if(lastResponse !== 'summary'){
      history.push(
        generateUserChat({ log, type: "system_model" })
      );
    }
    lastResponse = 'others'
    
    if (log.status === "failed") {
      history.push(
        generateFailedChat({ log, type: "system_model" })
      );
      return history;
    }

    const llmResponse = log.llm_response || {};
    const content = {};

    if(llmResponse.question){
      history.push({
        id: String(log._id),
        role: "ai",
        status: true,
        content: llmResponse?.question?.text,
        type: "system_model",
        time: log.final_response_timestamp
      })

      return history
    } 
    if(llmResponse.summary){
      lastResponse = 'summary'
      history.push({
        id: String(log._id),
        role: "ai",
        status: true,
        children: <GenerationSummary 
          title="Generation Summary"
          sections={[
            {
              type: 'prompt',
              label: "From Your input",
              rows: llmResponse.summary?.from_prompt?.map(d => ({key: d.label, value: d.value}))
            },
            {
              type: 'answers',
              label: "From your answers",
              rows: llmResponse.summary?.from_answers?.map(d => ({key: d.label, value: d.value}))
            },
            {
              type: 'assumed',
              label: "Assumed by AI",
              rows: llmResponse.summary?.assumed_by_ai?.map(d => ({key: d.label, value: d.value}))
            }
          ]}
        />,
        type: "summary",
        time: log.final_response_timestamp
      })

      return history
    }

    Object.entries(FIELD_MAP).forEach(([key, label]) => {
      if (Array.isArray(llmResponse[key]) && llmResponse[key].length) {
        content[label] = llmResponse[key].map(({ name, description, helperText }) => ({
          name,
          description: helperText || description
        }));
      }
    });

    history.push({
      id: String(log._id),
      role: "ai",
      status: true,
      content,
      type: "system_model",
      time: log.final_response_timestamp
    });

    return history;
  }, []);
};



const buildSimulationChatHistory = (logs) => {
  const history = [];
  logs.forEach(log => {
    const response = log.llm_response;
    
    if(log.type === "simulation_clarity_check"){
      const llmResponse = log.llm_response
      // history.push(generateUserChat({ log, type: "view" }));
      if(llmResponse.question){
        if(log.userPrompt){
          history.push(
            generateUserChat({ log, type: "simulation" })
          );
        }
        history.push({
          id: String(log._id),
          role: "ai",
          status: true,
          content: typeof response === "string" ? response : llmResponse?.question?.text,
          type: "system_model",
          time: log.final_response_timestamp
        })
      }else if(llmResponse.summary){
        if(log.userPrompt){
          history.push(
            generateUserChat({ log, type: "simulation" })
          );
        }
        history.push({
          id: String(log._id),
          role: "ai",
          status: true,
          children: <GenerationSummary 
            title="Generation Summary"
            sections={[
              {
                type: 'prompt',
                label: "From Your input",
                rows: llmResponse.summary?.from_prompt?.map(d => ({key: d.label, value: d.value}))
              },
              {
                type: 'answers',
                label: "From your answers",
                rows: llmResponse.summary?.from_answers?.map(d => ({key: d.label, value: d.value}))
              },
              {
                type: 'assumed',
                label: "Assumed by AI",
                rows: llmResponse.summary?.assumed_by_ai?.map(d => ({key: d.label, value: d.value}))
              }
            ]}
          />,
          type: "summary",
          time: log.final_response_timestamp
        })
      } else if (log.status === "stopped") {
        history.push(generateUserChat({ log, type: "simulation" }));
        history.push(generateStoppedChat({log, type: "simulation"}));
      }
    }if (log.type === "simulation_scenarios") {

        if (log.userPrompt != null) {
          history.push(generateUserChat({ log, type: "simulation" }));
        }
        if (log.status === "failed") {
          history.push(generateFailedChat({ log, type: "simulation" }));
        } else if (log.status === "stopped") {
          history.push(generateStoppedChat({log, type: "simulation"}));
        } else {

          history.push({
            id: String(log._id),
            role: 'ai',
            status: true,
            // content: parseTestSuites(log.llm_response)?.length === 0 ? 'No results generated. Try refining your request.' : (parseTestSuites(log.llm_response) || []),
            content: typeof response === "string" ? response : (log.llm_response || []),
            type: 'simulation',
            time: log.final_response_timestamp
          });
        }
       
    } else if (log.type === "simulation_testsuites_update" && log.action === "update") {
      if (log.userPrompt != null) {
        history.push(generateUserChat({ log, type: "simulation" }));
        if (log.status === "failed") {
          history.push(generateFailedChat({ log, type: "simulation" }));
        } else {
          history.push({
            id: String(log._id),
            role: 'ai',
            status: true,
            content: typeof response === "string" ? response : "Simulation updated",
            type: 'simulation',
            time: log.final_response_timestamp
          });
        }
      }
    }
  });
  return history;
}




const parseTestSuites = (text) => {
  const suites = text.split(/Testsuite:/).filter(Boolean);

  return suites.map(block => {
    const lines = block.trim().split('\n');

    const title = lines[0].trim();

    const description = lines
      .slice(1)
      .join(' ')
      .replace("Description:", "")
      .trim();

    return {
      name: slugify(title.toLowerCase()),
      description
    };
  });
};

export const convertToTestsuiteString = (testSuites) => {
  return testSuites.filter(item => item?.name)
    .map(
      (item) =>
        `Testsuite: ${formatName(item.name)}  \nDescription: ${item.description}`
    )
    .join("\n\n");
}

function formatName(name) {
  return name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}


const slugify = (str) => {
  if (typeof str !== 'string') return '';

  return str
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}\s_-]/gu, '')
    .trim()
    .replace(/[\s_-]+/g, '_')
    .replace(/^-+|-+$/g, '');
}





export const checkStatusofAiAssistantWorkflow = ({ stepKey, clusterlets = [], transactions = [], events = [], databaseProfiles = [], views = [], testSuites = [] }) => {
  if (stepKey === "system_model") {
    const filteredClusterlet = clusterlets.filter(item => item.subType !== "controller");
    return filteredClusterlet.length > 0;
  }

  if (stepKey === "database_profile") {
    return databaseProfiles.some(item => item.attributes?.tables?.length > 0);
  }

  if (stepKey === "view") {
    return views.length > 0;
  }

  if (stepKey === "transaction") {

    const functionalTransactions = getFunctionalTransactions({ transactions, events, clusterlets })
    return functionalTransactions.length > 0;
  }

  if (stepKey === "simulation") {
    return testSuites.length > 0;
  }

  return false;
};


export const buildWorkflowSteps = (steps, data, assistantContext) => {
  // 1. Initial completion check
  let updatedSteps = steps.map(step => {
    const isCompleted = checkStatusofAiAssistantWorkflow({
      stepKey: step.key,
      ...data
    });

    return {
      ...step,
      status: isCompleted ? "Completed" : "Pending",
      type: isCompleted ? "completed" : "pending",
      isEnabled: isCompleted ? true : false
    };
  });

  // 2. Backfill completion (bottom → top)
  let foundCompletedBelow = false;

  for (let i = updatedSteps.length - 1; i >= 0; i--) {
    if (updatedSteps[i].type === "completed") {
      foundCompletedBelow = true;
    }

    if (foundCompletedBelow) {
      updatedSteps[i] = {
        ...updatedSteps[i],
        status: "Completed",
        type: "completed",
        isEnabled: true
      };
    }
  }


  // 3. Set active step
  let activeSet = false;
  let activeStep = "system_model";
  updatedSteps = updatedSteps.map(step => {
    if (!activeSet && step.type === "pending") {
      activeSet = true;
      activeStep = step.key;
      return {
        ...step,
        isEnabled: true
      };
    }

    return step;
  });
  // 4. Edge case: all completed → use assistantContext
  const allCompleted = updatedSteps.every(step => step.type === "completed");
  const selectedStep = assistantContext?.entityType || "system_model";
  if (allCompleted || selectedStep) {
    updatedSteps = updatedSteps.map(step => {
      if (step.key === selectedStep) {
        activeStep = step.key;
        return {
          ...step,
          isEnabled: true
        };
      }
      return step;
    });
  }

  return { updatedSteps, activeStep };
};



export const overrideActiveStep = (steps, dataKey) => {
  return steps.map(step => {
    if (step.type === "in_progress" && step.key !== dataKey) {
      return {
        ...step,
        type: "completed",
        status: "Completed"
      };
    }

    if (step.key === dataKey) {
      return {
        ...step,
        type: "in_progress",
        status: ""
      };
    }

    return step;
  });
};


export const OWNERSHIP_STATUS = {
  AI_GENERATED: 'AI_GENERATED',
  AI_UPDATED: 'AI_UPDATED'
};

export const getOwnershipStatus = (provenance = {}) => {
  const {
      createdBy,
      manualModified = false,
      aiModified = false
  } = provenance;


  // AI generated with no manual changes
  if (createdBy === 'AI') {
      return OWNERSHIP_STATUS.AI_GENERATED;
  }


  // Manual entity updated by AI
  if (createdBy === 'User' && aiModified) {
      return OWNERSHIP_STATUS.AI_UPDATED;
  }

  return null;
}


export const shouldShowAIBadge = (provenance={}) => {
  return provenance?.createdBy === 'AI' || provenance?.aiModified === true;
}
